import { useCallback, useEffect, useState } from "react";

function ruta(prefix: string, seccion: string): string {
  return `#/${prefix}/${seccion}`;
}

function hashActual(): string {
  return typeof window === "undefined" ? "" : window.location.hash;
}

function parseHash<T extends string>(prefix: string, secciones: readonly T[], fallback: T): T {
  const limpio = hashActual().replace(/^#\/?/, "");
  const [actualPrefix, actualSeccion] = limpio.split("/");
  if (actualPrefix !== prefix) return fallback;
  return secciones.includes(actualSeccion as T) ? (actualSeccion as T) : fallback;
}

export function useHashSection<T extends string>(
  prefix: string,
  secciones: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const [seccion, setSeccionState] = useState<T>(() => parseHash(prefix, secciones, fallback));

  useEffect(() => {
    const normalizada = ruta(prefix, parseHash(prefix, secciones, fallback));
    if (hashActual() !== normalizada) {
      window.history.replaceState(null, "", normalizada);
    }
    const sync = () => {
      setSeccionState(parseHash(prefix, secciones, fallback));
    };
    window.addEventListener("hashchange", sync);
    sync();
    return () => {
      window.removeEventListener("hashchange", sync);
    };
  }, [fallback, prefix, secciones]);

  const setSeccion = useCallback((next: T) => {
    const nextHash = ruta(prefix, next);
    if (hashActual() === nextHash) {
      setSeccionState(next);
      return;
    }
    window.location.hash = nextHash;
  }, [prefix]);

  return [seccion, setSeccion];
}
